import type { LocalFile, ReplaceSecureNoteInput } from '@frontend/api-types';
import type { SnakeCasedProperties } from '@frontend/duck-tape';
import type { SecureNoteFormSchema } from '@frontend/forms';
import {
  getFileExceededError,
  getLocalFiles,
  getSecureNoteFormDefaultValues,
  useSecureNoteForm,
} from '@frontend/forms';
import type { ModalProps } from '@frontend/web-react';
import {
  Button,
  DragAndDropModal,
  FileInput,
  Modal,
  PasswordInput,
  showToast,
  TextareaInput,
  TextInput,
  YStack,
} from '@frontend/web-react';
import { WebTypedFormData } from '@frontend/web-utils/webTypedFormData';
import { useCreateSecureNoteMutation, useReplaceSecureNoteContentMutation } from '@frontend/web/hooks';
import { handleMutation } from '@frontend/web/utils';

export const CreateSecureNoteModal = ({
  clientId,
  isOpen,
  onClose,
}: Pick<ModalProps, 'isOpen' | 'onClose'> & { clientId: string }) => {
  const [createSecureNote, { isLoading: isCreating }] = useCreateSecureNoteMutation();
  const [updateSecureNoteContent, { isLoading: isUpdating }] = useReplaceSecureNoteContentMutation();

  const onSubmitCreateSecureNote = async (values: SecureNoteFormSchema) => {
    const { attachments, notes, password, title, username, website } = values;
    handleMutation({
      mutation: async () => {
        const secureNote = await createSecureNote({ client: clientId, title }).unwrap();
        if (secureNote.id) {
          const formData = new WebTypedFormData<SnakeCasedProperties<ReplaceSecureNoteInput>>();
          if (attachments.length) formData.appendLocalFiles('attachments', attachments as LocalFile[]);
          if (notes) formData.append('notes', notes);
          if (password) formData.append('password', password);
          if (username) formData.append('username', username);
          if (website) formData.append('website', website);

          await updateSecureNoteContent({ formData, secureNoteId: secureNote.id }).unwrap();
        }
        return { secureNoteId: secureNote.id };
      },
      onSuccess: onClose,
      successMessage: 'Successfully created.',
    });
  };

  const { dirty, getControl, isValid, setFieldValue, submitForm, values } = useSecureNoteForm({
    initialValues: getSecureNoteFormDefaultValues(),
    onSubmit: onSubmitCreateSecureNote,
    validateOnMount: false,
  });

  // Using the validator to handle this logic is tricky, this is a quick and dirty solution
  const atLeastOneValueIsSet =
    values.attachments.length || values.notes || values.password || values.username || values.website;

  return (
    <Modal headerGap="none" isOpen={isOpen} onClose={onClose} title="Create Secure Note">
      <YStack className="gap-y-md">
        <TextInput
          label="Title"
          placeholder="Ex: Amazon Account"
          value={getControl('title').value ?? ''}
          {...getControl('title', ['value'])}
        />
        <TextInput
          label="Email or username"
          placeholder="Email or username"
          value={getControl('username').value ?? ''}
          {...getControl('username', ['value'])}
        />
        <PasswordInput
          label="Password"
          value={getControl('password').value ?? ''}
          {...getControl('password', ['value'])}
        />
        <TextInput
          label="Website"
          placeholder="getduckbill.com"
          value={getControl('website').value ?? ''}
          {...getControl('website', ['value'])}
        />
        <FileInput
          allowedFileTypes={['image', 'pdf']}
          onChange={(files) => {
            const localFiles = getLocalFiles(files);
            const fileExceededError = getFileExceededError(localFiles);
            if (fileExceededError) {
              showToast({ title: fileExceededError, variant: 'danger' });
            } else {
              setFieldValue('attachments', files);
            }
          }}
          previewType="listItems"
          showPreview
          {...getControl('attachments', ['onChange'])}
        />
        <DragAndDropModal
          accept={{ IMAGE_MIME_TYPE: [] }}
          onFilesDropped={(attachments) => {
            const fileExceededError = getFileExceededError(attachments);
            if (fileExceededError) {
              showToast({ title: fileExceededError, variant: 'danger' });
            } else {
              setFieldValue('attachments', [...values.attachments, ...attachments]);
            }
          }}
        />
        <TextareaInput
          label="Notes"
          placeholder="Anything else we should know?"
          {...getControl('notes')}
          showOptionalText
        />

        <Button
          disabled={!isValid || !dirty || !values.title || !atLeastOneValueIsSet}
          isLoading={isCreating || isUpdating}
          label="Create"
          onClick={submitForm}
        />
      </YStack>
    </Modal>
  );
};
